import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Divider,
  CircularProgress,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar, ListItemAvatar,
  InputAdornment, // Added for search icon
  Tooltip // Added for disabled buttons
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  People as PeopleIcon, AdminPanelSettings, CategoryOutlined, HomeOutlined, VpnKey, Star, Flag,
  AddCircleOutline as AddCircleOutlineIcon, // Added for assign button
  Search as SearchIcon // Added for search input
} from '@mui/icons-material';
import { ChromePicker } from 'react-color';
import roleAdminService from '../../services/roleAdminService';
import userService from '../../services/userServices'; // <-- Import user service (ensure this exists)
import permissionService from '../../services/permissionService';
import ApartmentIcon from '@mui/icons-material/Apartment';
import _debounce from 'lodash/debounce'; // <-- Import debounce
import PermissionManager from './PermissionManager'; // <-- Ajouter ici

// Liste des icônes disponibles
const availableIcons = [
  { value: 'AdminPanelSettings', label: 'Admin', icon: <AdminPanelSettings /> },
  { value: 'Security', label: 'Sécurité', icon: <SecurityIcon /> },
  { value: 'People', label: 'Utilisateurs', icon: <PeopleIcon /> },
  { value: 'Category', label: 'Catégories', icon: <CategoryOutlined /> },
  { value: 'Edit', label: 'Édition', icon: <EditIcon /> },
  { value: 'Flag', label: 'Modération', icon: <Flag /> },
  { value: 'Home', label: 'Propriétaire', icon: <HomeOutlined /> },
  { value: 'Apartment', label: 'Locataire', icon: <ApartmentIcon /> },
  { value: 'Star', label: 'Par défaut', icon: <Star /> },
  { value: 'VpnKey', label: 'Clé/Permission', icon: <VpnKey /> } // Added VpnKey explicitly
];

// Fonction pour obtenir l'icône correspondant au nom d'icône stocké dans le rôle
const getIconComponent = (iconName) => {
  const foundIcon = availableIcons.find(icon => icon.value === iconName);
  return foundIcon ? foundIcon.icon : <VpnKey />; // Default to VpnKey if not found
};
/**
 * Composant pour la gestion des rôles
 * @returns {React.ReactNode} - Composant de gestion des rôles
 */
const RoleManagement = () => {
  const [selectedExistingPermissions, setSelectedExistingPermissions] = useState([]);
  const [newPermissions, setNewPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [fetchedPermissions, setFetchedPermissions] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPermissionsDialog, setOpenPermissionsDialog] = useState(false);
  const [openUsersDialog, setOpenUsersDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' ou 'edit'
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isSystem: false,
    isActive: true,
    displayOrder: 100,
    icon: 'Star',
    color: '#607D8B'
  });
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [tabValue, setTabValue] = useState(0);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [roleUsers, setRoleUsers] = useState([]); // Users currently assigned the role
  const [loadingUsers, setLoadingUsers] = useState(false); // Loading assigned users
  // --- State for User Search ---
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]); // Users found by search
  const [loadingSearch, setLoadingSearch] = useState(false); // Loading search results
  // --- End State for User Search ---
  const tableContainerRef = useRef(null);
  const newRoleButtonRef = useRef(null);

  // Charger les rôles et permissions au montage du composant
  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  useEffect(() => {
    return () => {
      setSearchResults([]);
    };
  }, [openDialog]);
  /**
   * Récupérer tous les rôles
   */
  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await roleAdminService.getAllRoles();
      console.log('Rôles récupérés', data);
      // Ensure data is sorted initially
      data.sort((a, b) => {
        const orderA = a.displayOrder === undefined || a.displayOrder === null ? 100 : a.displayOrder;
        const orderB = b.displayOrder === undefined || b.displayOrder === null ? 100 : b.displayOrder;
        const orderDiff = orderA - orderB;
        if (orderDiff !== 0) return orderDiff;
        return a.name.localeCompare(b.name);
      });
      setRoles(data); // This triggers a re-render
    } catch (error) {
      console.error('Error fetching roles:', error);
      showNotification('Erreur lors du chargement des rôles', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Récupérer toutes les permissions
   */
  const fetchPermissions = async () => {
    try {
      const data = await permissionService.getAllPermissions();
      setPermissions(data); // This triggers a re-render
    } catch (error) {
      console.error('Error fetching permissions:', error);
      // Optionally show notification for permission fetch error
      // showNotification('Erreur lors du chargement des permissions', 'error');
    }
  };

// Après la fonction fetchRoles, ajouter :
  const handleSearchResults = (results) => {
    // Filtrer les résultats pour exclure les permissions déjà existantes
    const filteredResults = results.filter(result =>
        !permissions.some(p => p.id === result.id) &&
        !newPermissions.some(np => np.tempId === result.id)
    );
    setSearchResults(filteredResults);
  };

  /**
   * Récupérer les utilisateurs d'un rôle
   * @param {string} roleId - ID du rôle
   */
  const fetchRoleUsers = async (roleId) => {
    if (!roleId) return;
    try {
      setLoadingUsers(true);
      const users = await roleAdminService.getRoleUsers(roleId);
      setRoleUsers(users);
    } catch (error) {
      console.error('Error fetching role users:', error);
      showNotification('Erreur lors du chargement des utilisateurs du rôle', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  /**
   * Ouvrir le dialogue de création
   */
  const handleOpenCreateDialog = () => {
    setDialogMode('create');

    // Réinitialisation complète de tous les états liés aux permissions
    setSelectedExistingPermissions([]);
    setNewPermissions([]);
    setFetchedPermissions([]);

    // Réinitialisation du formulaire avec valeurs par défaut
    setFormData({
      name: '',
      description: '',
      isSystem: false,
      isActive: true,
      displayOrder: 100,
      icon: 'Star',
      color: '#607D8B'
    });

    // Nettoyage des résultats de recherche précédents
    if (typeof handleSearchResults === 'function') {
      handleSearchResults([]);
    }

    // Forcer le rechargement des permissions disponibles
    fetchPermissions().catch(error => {
      console.error('Error refreshing permissions:', error);
      showNotification('Erreur lors du chargement des permissions', 'error');
    });

    setOpenDialog(true);

    // Focus sur le premier champ après ouverture (accessibilité)
    setTimeout(() => {
      const nameInput = document.querySelector('#role-name-input');
      if (nameInput) nameInput.focus();
    }, 100);
  };

  /**
   * Ouvrir le dialogue d'édition
   * @param {Object} role - Rôle à éditer
   */
  const handleOpenEditDialog = (role) => {
    setDialogMode('edit');
    setSelectedRole(role);

    // Récupérer les IDs des permissions existantes
    const existingPermissionIds = role.directPermissions?.map(p => p.id) || [];

    // Initialiser les states
    setSelectedExistingPermissions(existingPermissionIds);
    setNewPermissions([]); // Réinitialiser les nouvelles permissions

    setFormData({
      name: role.name,
      description: role.description || '',
      isSystem: role.isSystem || false,
      isActive: role.isActive !== undefined ? role.isActive : true,
      displayOrder: role.displayOrder === undefined || role.displayOrder === null ? 100 : role.displayOrder,
      icon: role.icon || 'Star',
      color: role.color || '#607D8B'
    });

    setOpenDialog(true);
  };

  /**
   * Ouvrir le dialogue de gestion des permissions (VERSION AVEC LOOKUP INTERNE)
   * @param {string} roleId - L'ID du rôle à ouvrir
   */
  const handleOpenPermissionsDialog = (roleId) => {
    // 1. Validation de l'ID reçu
    if (!roleId) {
      console.error("handleOpenPermissionsDialog: Invalid roleId received.", roleId);
      showNotification("Impossible d'ouvrir les permissions : ID du rôle manquant.", "error");
      return;
    }

    console.log(`handleOpenPermissionsDialog (Lookup Inside) called for roleId: ${roleId}`);

    // 2. Trouver l'objet rôle le plus récent dans l'état 'roles' EN UTILISANT L'ID
    //    This uses the 'roles' state as it exists when the function is called.
    const role = roles.find(r => r.id === roleId);

    // 3. Valider si le rôle a été trouvé dans l'état
    if (!role) {
      console.error(`handleOpenPermissionsDialog: Role with ID ${roleId} not found in current 'roles' state!`);
      showNotification("Erreur : Impossible de trouver les données actuelles du rôle.", "error");
      return;
    }
    console.log("Found role object from state inside handler:", role);


    // 4. Vérifier si les permissions sont présentes dans l'objet rôle trouvé
    if (!role.directPermissions || !Array.isArray(role.directPermissions)) {
      console.warn("handleOpenPermissionsDialog: 'directPermissions' array is missing or invalid in the found role object.", role.directPermissions);
      // Attempt to show the dialog even if permissions are missing, maybe they'll load?
      // Or return early as before:
      showNotification("Impossible de charger les permissions : données manquantes dans l'objet rôle.", "error");
      return; // Arrêter si les permissions ne sont pas là
    }

    // 5. Extraire les IDs des permissions directement depuis l'objet rôle trouvé
    const rolePermissionIds = role.directPermissions.map(p => p.id);
    console.log("Permissions found directly in role object. IDs:", rolePermissionIds);

    // 6. Mettre à jour les états pour ouvrir le dialogue
    setSelectedRole(role); // Utiliser l'objet rôle trouvé
    setSelectedPermissions(rolePermissionIds); // Définir les permissions cochées
    setTabValue(0); // Réinitialiser l'onglet
    setOpenPermissionsDialog(true); // Ouvrir le dialogue

    console.log("Permissions dialog should open with correct permissions checked.");
  };

  /**
   * Ouvrir le dialogue de gestion des utilisateurs assignés
   * @param {Object} role - Rôle sélectionné
   */
  const handleOpenUsersDialog = (role) => {
    setSelectedRole(role);
    setOpenUsersDialog(true);
    fetchRoleUsers(role.id); // Fetch users when opening the dialog
    // Reset search state
    setUserSearchQuery('');
    setUserSearchResults([]);
    setLoadingSearch(false);
  };

  /**
   * Fermer les dialogues
   */
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRole(null); // Reset selected role when closing main dialog
    setShowColorPicker(false); // Ensure color picker closes
  };

  const handleClosePermissionsDialog = () => {
    setOpenPermissionsDialog(false);
    setSelectedRole(null); // Reset selected role when closing permissions dialog
    setSelectedPermissions([]);
  };

  const handleCloseUsersDialog = () => {
    setOpenUsersDialog(false);
    setSelectedRole(null); // Reset selected role when closing users dialog
    setRoleUsers([]);
    // Reset search state when closing
    setUserSearchQuery('');
    setUserSearchResults([]);
    setLoadingSearch(false);
  };

  /**
   * Gérer les changements dans le formulaire
   * @param {Object} e - Événement de changement
   */
  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' || type === 'switch' ? checked : value
    }));
  };

  /**
   * Gérer le changement de couleur
   * @param {Object} color - Objet couleur de react-color
   */
  const handleColorChange = (color) => {
    setFormData(prev => ({
      ...prev,
      color: color.hex
    }));
  };

  /**
   * Gérer le changement d'onglet dans le dialogue des permissions
   * @param {Object} event - Événement de changement
   * @param {number} newValue - Nouvel index d'onglet
   */
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  /**
   * Gérer la sélection/désélection d'une permission
   * @param {string} permissionId - ID de la permission
   */
  const handleTogglePermission = (permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  /**
   * Soumettre le formulaire de rôle (création ou mise à jour)
   */
  const handleSubmit = async () => {
    // Validation de base
    if (!formData.name?.trim()) {
      showNotification('Le nom du rôle est requis', 'warning');
      return;
    }

    // Validation des permissions
    if (selectedExistingPermissions.length + newPermissions.length > 20) {
      showNotification('Maximum 20 permissions par rôle', 'error');
      return;
    }

    if (newPermissions.some(p => !p.name.match(/^[a-z_]{3,50}$/))) {
      showNotification('Format de permission invalide', 'error');
      return;
    }

    // Calculer les valeurs avant les appels API
    const existingPermissions = permissions.filter(p =>
        selectedExistingPermissions.includes(p.id)
    );

    const createdPermissions = newPermissions.map(np => ({
      id: np.tempId,
      name: np.name,
      description: np.description,
      category: np.category
    }));

    // Construction du payload
    const payload = {
      ...formData,
      permissions: {
        attach: [
          ...selectedExistingPermissions.filter(id =>
              typeof id === 'string' && id.startsWith('temp-') === false
          ),
          ...newPermissions.map(p => p.tempId)
        ],
        create: newPermissions.map(p => ({
          name: p.name,
          description: p.description,
          category: p.category || 'custom'
        }))
      }
    };
    let response;
    try {


      if (dialogMode === 'create') {
        // Création du rôle
        response = await roleAdminService.createRole(payload);
        showNotification('Rôle créé avec succès', 'success');

        // Mise à jour optimiste
        setRoles(prev => {
          const newRoleWithPermissions = {
            ...response,
            directPermissions: [
              ...existingPermissions,
              ...createdPermissions
            ]
          };
          return [...prev, newRoleWithPermissions].sort((a, b) => {
            const orderA = a.displayOrder ?? 100;
            const orderB = b.displayOrder ?? 100;
            return orderA - orderB || a.name.localeCompare(b.name);
          });
        });

        // Réinitialisation complète
        setNewPermissions([]);
        setSelectedExistingPermissions([]);
        handleCloseDialog();

        // Rechargement des données fraîches
        await Promise.all([fetchRoles(), fetchPermissions()]);

      } else {
        // Mise à jour du rôle
        const updateData = { ...payload };
        if (selectedRole.isSystem) {
          delete updateData.isSystem;
          delete updateData.name;
        }

        response = await roleAdminService.updateRole(selectedRole.id, updateData);
        showNotification('Rôle mis à jour avec succès', 'success');

        // Mise à jour optimiste locale
        setRoles(prev =>
            prev.map(r =>
                r.id === selectedRole.id ? {
                  ...r,
                  ...response,
                  directPermissions: [
                    ...existingPermissions,
                    ...createdPermissions
                  ]
                } : r
            )
        );

        // Mise à jour des permissions globales
        if (newPermissions.length > 0) {
          setPermissions(prev => [
            ...prev,
            ...newPermissions.map(np => ({
              id: np.tempId,
              name: np.name,
              description: np.description,
              category: np.category
            }))
          ]);
        }

        handleCloseDialog();
      }

      // Gestion des avertissements
      if (response?.warnings?.permissions) {
        showNotification(response.warnings.permissions.join(', '), 'warning');
      }

    } catch (error) {
      console.error('Erreur lors de la soumission :', error);

      // Rollback des modifications optimistes
      if (dialogMode === 'create') {
        // Utiliser l'ID de réponse uniquement si disponible
        const responseId = response?.id;
        if (responseId) {
          setRoles(prev => prev.filter(r => r.id !== responseId));
        }
      } else {
        // Rollback basé sur le rôle sélectionné existant
        setRoles(prev => prev.map(r =>
            r.id === selectedRole.id ? selectedRole : r
        ));
      }


      // Gestion d'erreur
      const errorMessage = error.response?.data?.message ||
          error.message ||
          'Erreur lors de la sauvegarde du rôle';

      showNotification(
          error.response?.data?.errorType === 'PERMISSION_ERROR'
              ? `Erreur de permissions : ${errorMessage}`
              : errorMessage,
          'error'
      );

      // Réinitialisation partielle
      if (dialogMode === 'create') {
        setNewPermissions([]);
        setSelectedExistingPermissions([]);
      }
    }
  };


  /**
   * Soumettre les changements de permissions pour le rôle sélectionné
   */
  const handleSubmitPermissions = async () => {
    if (!selectedRole) return;
    console.log("handleSubmitPermissions: Saving permissions for role:", selectedRole.id);
    console.log("Selected permission IDs to save:", selectedPermissions);

    try {
      // Récupérer les permissions actuelles du rôle (IDs only) depuis l'objet fullRole chargé
      const currentPermissionIds = selectedRole.directPermissions?.map(p => p.id) || [];
      console.log("Current permission IDs from selectedRole:", currentPermissionIds);

      // Permissions à ajouter
      const permissionsToAdd = selectedPermissions.filter(
          id => !currentPermissionIds.includes(id)
      );
      console.log("Permissions to ADD:", permissionsToAdd);

      // Permissions à supprimer
      const permissionsToRemove = currentPermissionIds.filter(
          id => !selectedPermissions.includes(id)
      );
      console.log("Permissions to REMOVE:", permissionsToRemove);

      // Exécuter les appels API en parallèle
      await Promise.all([
        ...permissionsToAdd.map(permissionId => {
          console.log(` -> Assigning permission ${permissionId} to role ${selectedRole.id}`);
          return roleAdminService.assignPermissionToRole(selectedRole.id, permissionId);
        }),
        ...permissionsToRemove.map(permissionId => {
          console.log(` -> Revoking permission ${permissionId} from role ${selectedRole.id}`);
          return roleAdminService.revokePermissionFromRole(selectedRole.id, permissionId);
        })
      ]);

      showNotification('Permissions du rôle mises à jour avec succès', 'success');
      handleClosePermissionsDialog();

      // --- IMPORTANT: Refetch roles pour mettre à jour l'aperçu dans le tableau principal ---
      console.log("handleSubmitPermissions: Refetching roles list after permission update...");
      fetchRoles(); // Re-fetch roles to update the preview in the main table

    } catch (error) {
      console.error('Error updating role permissions:', error);
      const errorMsg = error.response?.data?.message || 'Erreur lors de la mise à jour des permissions du rôle';
      showNotification(errorMsg, 'error');
    }
  };

  /**
   * Révoquer un rôle d'un utilisateur spécifique
   * @param {string} userId - ID de l'utilisateur
   */
  const handleRevokeRoleFromUser = async (userId) => {
    if (!selectedRole || !userId) return;
    // TODO: Replace window.confirm with a Material UI Dialog for better UX
    if (window.confirm(`Êtes-vous sûr de vouloir révoquer le rôle "${selectedRole.name}" de cet utilisateur ?`)) {
      try {
        await roleAdminService.revokeRoleFromUser(userId, selectedRole.id);
        showNotification('Rôle révoqué avec succès', 'success');
        fetchRoleUsers(selectedRole.id); // Refresh the list of assigned users in the dialog
        // Optionally, refresh search results if the user might reappear there
        // Consider if debouncedSearchUsers needs re-triggering or state update
      } catch (error) {
        console.error('Error revoking role from user:', error);
        const errorMsg = error.response?.data?.message || 'Erreur lors de la révocation du rôle';
        showNotification(errorMsg, 'error');
      }
    }
  };

  /**
   * Supprimer un rôle (ou le désactiver s'il est utilisé)
   * @param {Object} role - Rôle à supprimer
   */
  const handleDeleteRole = async (role) => {
    if (!role || role.isSystem) return; // Double check system role
    // TODO: Replace window.confirm with a Material UI Dialog for better UX
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le rôle "${role.name}" ? S'il est utilisé, il sera désactivé.`)) {
      try {
        const response = await roleAdminService.deleteRole(role.id);
        // Use message from backend if available, otherwise default
        const successMsg = response?.message || 'Opération de suppression/désactivation réussie';
        showNotification(successMsg, 'success');
        fetchRoles(); // Refresh the main list
      } catch (error) {
        console.error('Error deleting role:', error);
        const errorMsg = error.response?.data?.message || 'Erreur lors de la suppression/désactivation du rôle';
        showNotification(errorMsg, 'error');
      }
    }
  };

  // --- User Search and Assign Functions ---

  /**
   * Handles changes in the user search input field and triggers debounced search.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event.
   */
  const handleUserSearchChange = (e) => {
    const query = e.target.value;
    setUserSearchQuery(query);
    // Trigger debounced search
    debouncedSearchUsers(query);
  };

  /**
   * Performs the user search via API, excluding already assigned users.
   * @param {string} query - The search query.
   */
  const searchUsers = async (query) => {
    // Only search if query is long enough and a role is selected in the dialog
    if (!query || query.trim().length < 2 || !selectedRole) {
      setUserSearchResults([]);
      setLoadingSearch(false);
      return;
    }
    setLoadingSearch(true);
    try {
      // Ensure userService and searchUsers function exist
      if (!userService || typeof userService.searchUsers !== 'function') {
        throw new Error("userService.searchUsers is not defined or not a function.");
      }
      // Call the service function, passing query and the ID of the role being managed
      const results = await userService.searchUsers(query, selectedRole.id);

      // Filter results client-side as a fallback/verification
      // Create a Set of IDs for users already assigned this role for efficient lookup
      const assignedUserIds = new Set(roleUsers.map(u => u.id));
      const filteredResults = results.filter(user => !assignedUserIds.has(user.id));

      setUserSearchResults(filteredResults);
    } catch (error) {
      console.error('Error searching users:', error);
      showNotification('Erreur lors de la recherche d\'utilisateurs', 'error');
      setUserSearchResults([]); // Clear results on error
    } finally {
      setLoadingSearch(false);
    }
  };

  // Debounced version of searchUsers function
  // useCallback ensures the debounced function is stable unless dependencies change
  const debouncedSearchUsers = useCallback(_debounce(searchUsers, 500), [selectedRole, roleUsers]); // Dependencies: selectedRole and roleUsers

  /**
   * Assigns the currently selected role (in the dialog) to a specific user.
   * @param {string} userId - The ID of the user to assign the role to.
   */
  const handleAssignRoleToUser = async (userId) => {
    if (!selectedRole || !userId) return;
    try {
      await roleAdminService.assignRoleToUser(userId, selectedRole.id);
      showNotification(`Rôle "${selectedRole.name}" assigné avec succès`, 'success');
      // Refresh the list of users currently assigned this role
      fetchRoleUsers(selectedRole.id);
      // Clear search input and results after successful assignment
      setUserSearchQuery('');
      setUserSearchResults([]);
    } catch (error) {
      console.error('Error assigning role to user:', error);
      const errorMsg = error.response?.data?.message || 'Erreur lors de l\'assignation du rôle';
      showNotification(errorMsg, 'error');
    }
  };
  // --- End User Search and Assign Functions ---


  /**
   * Afficher une notification Snackbar
   * @param {string} message - Message à afficher
   * @param {'success' | 'error' | 'warning' | 'info'} severity - Type de notification
   */
  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  /**
   * Fermer la notification Snackbar
   */
  const handleCloseNotification = (event, reason) => {
    // Prevent closing on click away
    // if (reason === 'clickaway') {
    //   return;
    // }
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  // Grouper les permissions par catégorie pour l'affichage par onglets
  const permissionsByCategory = permissions.reduce((acc, permission) => {
    const category = permission.category || 'Autre'; // Default category if none provided
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(permission);
    return acc;
  }, {});

  // Obtenir la liste des catégories triées (optionnel)
  const categories = Object.keys(permissionsByCategory).sort();

  // JSX Rendering
  return (
      <Box sx={{ p: 3 }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            Gestion des Rôles
          </Typography>
          <Button
              ref={newRoleButtonRef}
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
          >
            Nouveau Rôle
          </Button>
        </Box>

        {/* Main Roles Table */}
        {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
        ) : (
            <TableContainer component={Paper} elevation={3} ref={tableContainerRef} tabIndex={-1}>
              <Table stickyHeader aria-label="roles table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Rôle</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Permissions (Aperçu)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roles.map((role) => (
                      <TableRow key={role.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        {/* Role Name + Icon */}
                        <TableCell component="th" scope="row">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                                sx={{
                                  bgcolor: role.color || '#607D8B', // Default color
                                  width: 32,
                                  height: 32,
                                  fontSize: '1rem' // Adjust icon size if needed
                                }}
                                variant="rounded"
                            >
                              {getIconComponent(role.icon)}
                            </Avatar>
                            <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: role.isSystem ? 600 : 400, // Bolder for system roles
                                  color: role.color || 'text.primary' // Use role color or default text color
                                }}
                            >
                              {role.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        {/* Description */}
                        <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {role.description}
                        </TableCell>
                        {/* Type Chip */}
                        <TableCell>
                          <Chip
                              label={role.isSystem ? "Système" : "Personnalisé"}
                              color={role.isSystem ? "info" : "default"} // Use 'info' for system roles
                              size="small"
                              variant="outlined"
                          />
                        </TableCell>
                        {/* Status Chip */}
                        <TableCell>
                          <Chip
                              label={role.isActive ? "Actif" : "Inactif"}
                              color={role.isActive ? "success" : "error"}
                              size="small"
                          />
                        </TableCell>
                        {/* Permissions Preview */}
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 250 }}>
                            {/* --- Utiliser directPermissions pour l'aperçu --- */}
                            {role.directPermissions?.slice(0, 3).map((permission) => (
                                <Tooltip key={permission.id} title={permission.name} arrow>
                                  <Chip
                                      label={permission.name.length > 15 ? `${permission.name.substring(0, 12)}...` : permission.name} // Truncate long names
                                      size="small"
                                      variant="outlined"
                                  />
                                </Tooltip>
                            ))}
                            {role.directPermissions && role.directPermissions.length > 3 && (
                                <Tooltip title={`${role.directPermissions.length - 3} autre(s) permission(s)`} arrow>
                                  <Chip
                                      label={`+${role.directPermissions.length - 3}`}
                                      size="small"
                                      variant="outlined"
                                  />
                                </Tooltip>
                            )}
                            {(!role.directPermissions || role.directPermissions.length === 0) && (
                                <Typography variant="caption" color="textSecondary">Aucune</Typography>
                            )}
                          </Box>
                        </TableCell>
                        {/* Action Buttons */}
                        <TableCell align="right">
                          {/* Edit Button */}
                          <Tooltip title={role.isSystem ? "Les rôles système ne peuvent pas être modifiés" : "Modifier le rôle"} arrow>
                            <span> {/* Span needed for tooltip on disabled button */}
                              <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleOpenEditDialog(role)}
                                  disabled={role.isSystem}
                              >
                                <EditIcon fontSize="small"/>
                              </IconButton>
                            </span>
                          </Tooltip>
                          {/* Permissions Button (MODIFIED onClick) */}
                          <Tooltip title="Gérer les permissions" arrow>
                            <IconButton
                                color="secondary"
                                size="small"
                                onClick={() => {
                                  console.log(`!!! Permissions Icon CLICKED for role ID: ${role?.id}, Name: ${role?.name}`);
                                  // --- Pass ONLY the ID to the handler ---
                                  handleOpenPermissionsDialog(role.id);
                                }}
                            >
                              <SecurityIcon fontSize="small"/>
                            </IconButton>
                          </Tooltip>
                          {/* Users Button */}
                          <Tooltip title="Gérer les utilisateurs assignés" arrow>
                            <IconButton
                                color="info"
                                size="small"
                                onClick={() => handleOpenUsersDialog(role)}
                            >
                              <PeopleIcon fontSize="small"/>
                            </IconButton>
                          </Tooltip>
                          {/* Delete Button */}
                          <Tooltip title={role.isSystem ? "Les rôles système ne peuvent pas être supprimés" : "Supprimer / Désactiver le rôle"} arrow>
                             <span> {/* Span needed for tooltip on disabled button */}
                               <IconButton
                                   color="error"
                                   size="small"
                                   onClick={() => handleDeleteRole(role)}
                                   disabled={role.isSystem}
                               >
                                <DeleteIcon fontSize="small"/>
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                  ))}
                  {/* No Roles Found Row */}
                  {roles.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body1" color="textSecondary" sx={{ p: 3 }}>
                            Aucun rôle n'a été créé pour le moment.
                          </Typography>
                        </TableCell>
                      </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
        )}

        {/* Role Create/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{dialogMode === 'create' ? 'Nouveau Rôle' : 'Modifier Rôle'}</DialogTitle>
          <DialogContent dividers> {/* Add dividers for better spacing */}
            <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
              <TextField
                  fullWidth
                  label="Nom du rôle"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  margin="normal"
                  required
                  // Disable name editing for system roles
                  disabled={dialogMode === 'edit' && selectedRole?.isSystem}
                  helperText={dialogMode === 'edit' && selectedRole?.isSystem ? "Le nom d'un rôle système ne peut pas être modifié" : ""}
                  inputProps={{ maxLength: 50 }} // Add max length
              />
              <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  margin="normal"
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 255 }} // Add max length
              />
              <TextField
                  fullWidth
                  label="Ordre d'affichage"
                  name="displayOrder"
                  type="number"
                  value={formData.displayOrder}
                  onChange={handleFormChange}
                  margin="normal"
                  inputProps={{ min: 0, step: 1 }} // Allow 0, integer steps
              />
              <FormControl fullWidth margin="normal">
                <InputLabel id="icon-select-label">Icône</InputLabel>
                <Select
                    labelId="icon-select-label"
                    id="icon-select"
                    name="icon"
                    value={formData.icon}
                    onChange={handleFormChange}
                    label="Icône"
                    renderValue={(selectedValue) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getIconComponent(selectedValue)} {availableIcons.find(i => i.value === selectedValue)?.label}
                        </Box>
                    )}
                >
                  {availableIcons.map(icon => (
                      <MenuItem key={icon.value} value={icon.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {icon.icon} {icon.label}
                        </Box>
                      </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ mt: 2, mb: 1 }}>
                <TextField
                    fullWidth
                    label="Couleur"
                    name="color"
                    value={formData.color}
                    onClick={() => setShowColorPicker(!showColorPicker)} // Toggle picker
                    InputProps={{
                      startAdornment: (
                          <InputAdornment position="start">
                            <Box
                                sx={{
                                  width: 24, height: 24, borderRadius: '4px', // Use theme border radius?
                                  backgroundColor: formData.color,
                                  border: '1px solid rgba(0,0,0,0.2)',
                                  cursor: 'pointer'
                                }}
                            />
                          </InputAdornment>
                      ),
                      readOnly: true,
                      sx: { cursor: 'pointer' }
                    }}
                    variant="outlined" // Ensure consistent styling
                />
                {showColorPicker && (
                    <Box sx={{ position: 'relative', mt: 1 }}> {/* Use relative for easier positioning */}
                      <Box sx={{ position: 'absolute', zIndex: 2 }}> {/* Ensure picker is above */}
                        {/* Optional: Add an overlay to close picker on outside click */}
                        {/* <Box sx={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0 }} onClick={() => setShowColorPicker(false)} /> */}
                        <ChromePicker color={formData.color} onChangeComplete={handleColorChange} disableAlpha={true}/>
                      </Box>
                    </Box>
                )}
              </Box>
              <FormControlLabel
                  control={
                    <Switch
                        checked={formData.isActive}
                        onChange={handleFormChange}
                        name="isActive"
                        color="success" // Use success color for active state
                    />
                  }
                  label="Actif"
                  sx={{ mt: 1 }}
              />
              {/* Display isSystem status but keep it disabled */}
              {dialogMode === 'edit' && (
                  <FormControlLabel
                      control={ <Switch checked={formData.isSystem} name="isSystem" disabled /> }
                      label="Rôle système (non modifiable)"
                      sx={{ mt: 1, color: 'text.secondary' }}
                  />
              )}
            </Box>
            {/* Modifier la section du PermissionManager dans le JSX :*/}
            <PermissionManager
                existingPermissions={roles.flatMap(r => r.directPermissions)}
                selectedPermissions={selectedExistingPermissions}
                onSearch={async (query) => {
                  try {
                    const results = await permissionService.searchPermissions({
                      query,
                      category: 'custom'
                    });
                    handleSearchResults(results);
                    return results;
                  } catch (error) {
                    showNotification('Erreur lors de la recherche', 'error');
                    return [];
                  }
                }}
                onSelect={(perm) => {
                  // Gestion unifiée des types
                  const id = typeof perm === 'object' ? perm.tempId : perm;

                  if (!selectedExistingPermissions.includes(id)) {
                    setSelectedExistingPermissions(prev => [...prev, id]);

                    // Si c'est une nouvelle permission
                    if (typeof perm === 'object') {
                      setNewPermissions(prev => [...prev, perm]);
                    }
                  }
                }}
                onRemove={(id) => {
                  setSelectedExistingPermissions(prev => prev.filter(pId => pId !== id));

                  // Supprimer des nouvelles permissions si c'est un tempId
                  if (id.startsWith('temp-')) {
                    setNewPermissions(prev => prev.filter(p => p.tempId !== id));
                  }
                }}

                onCreate={(newPerm) => {
                  console.log('[RoleManagement] Tentative création permission:', newPerm);

                  // 1. Validation du format avec message d'erreur précis
                  if (!newPerm.name.match(/^[a-z0-9_]{3,50}$/)) {
                    showNotification('Format de permission invalide (uniquement lettres minuscules, chiffres et underscore)', 'error');
                    return;
                  }

                  // 2. Vérification des doublons améliorée
                  // Normaliser le nom pour la comparaison
                  const normalizedNewName = newPerm.name.toLowerCase().trim();

                  // Vérifier uniquement dans les permissions pertinentes
                  const relevantPermissions = [
                    // Permissions déjà sélectionnées pour ce rôle
                    ...selectedExistingPermissions
                        .filter(id => typeof id === 'string' && !id.startsWith('temp-'))
                        .map(id => permissions.find(p => p.id === id))
                        .filter(Boolean),
                    // Nouvelles permissions temporaires déjà créées
                    ...newPermissions
                  ];

                  console.log('[RoleManagement] Vérification doublons parmi:',
                      relevantPermissions.length, 'permissions pertinentes au lieu de',
                      permissions.length, 'permissions totales');

                  // Recherche de doublon avec comparaison normalisée
                  const duplicate = relevantPermissions.find(p =>
                      p && p.name && p.name.toLowerCase().trim() === normalizedNewName
                  );

                  if (duplicate) {
                    console.warn('[RoleManagement] Doublon détecté:', duplicate);
                    showNotification(`Cette permission existe déjà sous le nom "${duplicate.name}"`, 'error');
                    return;
                  }

                  // 3. Vérification supplémentaire dans toutes les permissions système
                  // mais uniquement avec un avertissement, pas un blocage
                  const systemDuplicate = permissions.find(p =>
                      p && p.name && p.name.toLowerCase().trim() === normalizedNewName
                  );

                  if (systemDuplicate && !duplicate) {
                    console.warn('[RoleManagement] Permission système similaire détectée:', systemDuplicate);
                    // Avertissement sans bloquer la création
                    showNotification(`Attention: Une permission système similaire existe déjà (${systemDuplicate.name})`, 'warning');
                  }

                  // 4. Création de la permission
                  console.log('[RoleManagement] Création permission validée:', newPerm);
                  setNewPermissions(prev => [...prev, newPerm]);
                  setSelectedExistingPermissions(prev => [...prev, newPerm.tempId || newPerm._id || newPerm.id]);

                  // 5. Confirmation visuelle
                  showNotification(`Permission "${newPerm.name}" créée avec succès`, 'success');
                }}
                sx={{
                  border: '1px solid #eee',
                  borderRadius: '8px',
                  padding: '16px',
                  mt: 2
                }}
            />

          </DialogContent>
          <DialogActions sx={{ p: '16px 24px' }}> {/* Add padding */}
            <Button onClick={handleCloseDialog} color="inherit">Annuler</Button>
            <Button onClick={handleSubmit} color="primary" variant="contained">
              {dialogMode === 'create' ? 'Créer' : 'Mettre à jour'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Permissions Management Dialog */}
        <Dialog open={openPermissionsDialog} onClose={handleClosePermissionsDialog} maxWidth="md" fullWidth>
          <DialogTitle>Permissions du rôle: {selectedRole?.name}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" aria-label="permission categories tabs">
                  {categories.map((category, index) => (
                      <Tab key={category} label={category} id={`perm-tab-${index}`} aria-controls={`perm-tabpanel-${index}`} />
                  ))}
                </Tabs>
              </Box>
              {categories.map((category, index) => (
                  <Box
                      key={category}
                      role="tabpanel"
                      hidden={tabValue !== index}
                      id={`perm-tabpanel-${index}`}
                      aria-labelledby={`perm-tab-${index}`}
                      sx={{ pt: 2 }}
                  >
                    {tabValue === index && (
                        <List dense>
                          {/* --- Vérification de la structure JSX pour l'affichage des permissions --- */}
                          {permissionsByCategory[category].map((permission, idx) => (
                              <React.Fragment key={permission.id}>
                                <ListItem disablePadding>
                                  <FormControlLabel
                                      sx={{ width: '100%', ml: 0 }}
                                      control={
                                        <Checkbox
                                            edge="start"
                                            checked={selectedPermissions.includes(permission.id)}
                                            onChange={() => handleTogglePermission(permission.id)}
                                            tabIndex={-1}
                                            disableRipple
                                            inputProps={{ 'aria-labelledby': `perm-label-${permission.id}` }}
                                        />
                                      }
                                      label={
                                        <ListItemText
                                            id={`perm-label-${permission.id}`}
                                            primary={permission.name}
                                            secondary={permission.description}
                                            sx={{ ml: 1 }}
                                        />
                                      }
                                  />
                                </ListItem>
                                {idx < permissionsByCategory[category].length - 1 && <Divider component="li" variant="inset" />}
                              </React.Fragment>
                          ))}
                        </List>
                    )}
                  </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: '16px 24px' }}>
            <Button onClick={handleClosePermissionsDialog} color="inherit">Annuler</Button>
            <Button onClick={handleSubmitPermissions} color="primary" variant="contained">
              Enregistrer
            </Button>
          </DialogActions>
        </Dialog>

        {/* Users Management Dialog (MODIFIED) */}
        <Dialog open={openUsersDialog} onClose={handleCloseUsersDialog} maxWidth="md" fullWidth>
          <DialogTitle>Gérer les utilisateurs pour le rôle: {selectedRole?.name}</DialogTitle>
          <DialogContent dividers>
            {/* --- Search Section --- */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'action.hover' }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Assigner à un utilisateur</Typography>
              <TextField
                  fullWidth
                  label="Rechercher un utilisateur (nom ou email, min 2 caractères)"
                  variant="outlined"
                  value={userSearchQuery}
                  onChange={handleUserSearchChange}
                  InputProps={{
                    startAdornment: ( <InputAdornment position="start"><SearchIcon /></InputAdornment> ),
                    endAdornment: ( loadingSearch ? <CircularProgress size={20} /> : null )
                  }}
                  sx={{ mb: 2 }}
              />
              {/* Search Results Area */}
              <Box sx={{ maxHeight: 200, overflowY: 'auto' }}> {/* Limit height and make scrollable */}
                {loadingSearch && userSearchResults.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}><CircularProgress size={24} /></Box>
                ) : !loadingSearch && userSearchQuery.length > 1 && userSearchResults.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', p: 1 }}>Aucun utilisateur trouvé ou tous sont déjà assignés.</Typography>
                ) : userSearchResults.length > 0 ? (
                    <List dense>
                      {userSearchResults.map((user) => (
                          <ListItem key={user.id} secondaryAction={
                            <Tooltip title="Assigner ce rôle" arrow>
                              <IconButton edge="end" color="primary" onClick={() => handleAssignRoleToUser(user.id)}>
                                <AddCircleOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          }>
                            <ListItemAvatar>
                              <Avatar src={user?.profilePicture} alt={user?.name}>{user?.name?.charAt(0).toUpperCase()}</Avatar>
                            </ListItemAvatar>
                            <ListItemText primary={user.name} secondary={user.email} />
                          </ListItem>
                      ))}
                    </List>
                ) : null}
              </Box>
            </Box>
            {/* --- End Search Section --- */}

            <Divider sx={{ my: 2 }}><Chip label="Utilisateurs Actuellement Assignés" /></Divider>

            {/* --- Assigned Users Section --- */}
            {loadingUsers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
            ) : roleUsers.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="textSecondary">Aucun utilisateur n'a ce rôle pour le moment.</Typography>
                </Box>
            ) : (
                <List>
                  {roleUsers.map((user, index) => ( // Added index for divider logic
                      <React.Fragment key={user?.id}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar src={user?.profilePicture} alt={user?.name}>{user?.name?.charAt(0).toUpperCase()}</Avatar>
                          </ListItemAvatar>
                          <ListItemText primary={user?.name} secondary={user?.email} />
                          <ListItemSecondaryAction>
                            <Tooltip title={selectedRole?.isSystem ? "Les rôles système ne peuvent pas être révoqués" : "Révoquer le rôle"} arrow>
                              <span> {/* Span needed for tooltip on disabled button */}
                                <IconButton
                                    edge="end"
                                    color="error"
                                    onClick={() => handleRevokeRoleFromUser(user?.id)}
                                    disabled={selectedRole?.isSystem}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {/* Add divider except for the last item */}
                        {index < roleUsers.length - 1 && <Divider variant="inset" component="li" />}
                      </React.Fragment>
                  ))}
                </List>
            )}
            {/* --- End Assigned Users Section --- */}

          </DialogContent>
          <DialogActions sx={{ p: '16px 24px' }}>
            <Button onClick={handleCloseUsersDialog} color="inherit">Fermer</Button>
          </DialogActions>
        </Dialog>

        {/* Notification Snackbar */}
        <Snackbar
            open={notification.open}
            autoHideDuration={6000}
            onClose={handleCloseNotification}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseNotification} severity={notification.severity} variant="filled" sx={{ width: '100%' }}>
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default RoleManagement;
